import { useEffect, useState } from 'react';
import { listUpcomingMowDays, signupForMowDay } from '../lib/api';
import type { MowDay } from '../lib/types';

const inputClass =
  'w-full bg-transparent border-b-2 border-forest/20 focus:border-terracotta focus:outline-none py-3 text-forest placeholder-forest/40 transition-colors';
const labelClass = 'block text-xs tracking-[0.2em] uppercase text-forest/70 mb-2';

function formatDate(iso: string): string {
  if (!iso) return '';
  // Parse as UTC date to avoid timezone shifting the displayed day.
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
  return dt.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export function MowDaySignup() {
  const [days, setDays] = useState<MowDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ ok: true } | { ok: false; error: string } | null>(null);

  useEffect(() => {
    (async () => {
      const res = await listUpcomingMowDays();
      if (res.ok) {
        const list = res.data.mow_days || [];
        setDays(list);
        if (list[0]) setSelectedId(list[0].id);
      }
      setLoading(false);
    })();
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setResult(null);
    if (!selectedId) {
      setResult({ ok: false, error: 'Pick a mow day to sign up for.' });
      return;
    }
    setSubmitting(true);
    const res = await signupForMowDay({
      mow_day_id: selectedId,
      first_name: first,
      last_name: last,
      email,
      phone,
      honeypot,
    });
    setSubmitting(false);
    if (res.ok) {
      setResult({ ok: true });
    } else {
      setResult({ ok: false, error: res.error || 'Could not sign up.' });
    }
  }

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-12 py-16">
      <div className="text-terracotta text-sm tracking-[0.25em] uppercase mb-3">Volunteer Mow Day</div>
      <h1
        className="text-5xl text-forest mb-4 leading-tight font-display"
        style={{ fontVariationSettings: '"opsz" 144' }}
      >
        Sign up for a <em>mow day.</em>
      </h1>
      <p className="text-lg text-forest/75 max-w-2xl mb-10 leading-relaxed">
        We gather on the first Saturday of every month to take care of yards
        for neighbors who need a hand. Pick a date below — once you're
        confirmed, you'll get route details by email a few days before.
      </p>

      {loading ? (
        <p className="text-forest/60">Loading upcoming dates…</p>
      ) : days.length === 0 ? (
        <p className="text-forest/60">No upcoming mow days are open right now. Check back soon.</p>
      ) : (
        <form onSubmit={submit} className="bg-cream border border-forest/10 p-8 lg:p-10 space-y-8">
          <div>
            <label className={labelClass}>Choose a date</label>
            <div className="grid sm:grid-cols-2 gap-3">
              {days.map((d) => {
                const closed = d.status !== 'open';
                return (
                  <button
                    key={d.id}
                    type="button"
                    disabled={closed}
                    onClick={() => setSelectedId(d.id)}
                    className={`py-4 px-4 border-2 text-left text-sm transition-colors ${
                      selectedId === d.id && !closed
                        ? 'border-terracotta bg-terracotta text-cream'
                        : closed
                        ? 'border-forest/15 text-forest/40 cursor-not-allowed'
                        : 'border-forest/20 text-forest hover:border-terracotta'
                    }`}
                  >
                    <div className="text-xs tracking-[0.15em] uppercase opacity-80 mb-1">
                      {closed ? 'Closed' : `${d.volunteer_count ?? 0} volunteers signed up`}
                    </div>
                    <div className="font-display text-lg leading-tight">{formatDate(d.event_date)}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className={labelClass}>First name *</label>
              <input
                className={inputClass}
                value={first}
                onChange={(e) => setFirst(e.target.value)}
                placeholder="First"
              />
            </div>
            <div>
              <label className={labelClass}>Last name *</label>
              <input
                className={inputClass}
                value={last}
                onChange={(e) => setLast(e.target.value)}
                placeholder="Last"
              />
            </div>
            <div>
              <label className={labelClass}>Email *</label>
              <input
                className={inputClass}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                inputMode="email"
              />
            </div>
            <div>
              <label className={labelClass}>Phone *</label>
              <input
                className={inputClass}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(361) 555-0123"
                inputMode="tel"
              />
            </div>
          </div>

          <p className="text-xs text-forest/60">
            New here? You'll need to{' '}
            <a href="/volunteer" className="text-terracotta underline">apply as a volunteer Mowkin</a>{' '}
            first. Once our team vets you, you can sign up for any open mow day.
          </p>

          {/* Honeypot */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
            aria-hidden="true"
          />

          {result && !result.ok && (
            <div className="bg-terracotta/10 border-l-4 border-terracotta p-4 text-terracotta text-sm">
              {result.error}
            </div>
          )}
          {result && result.ok && (
            <div className="bg-forest/10 border-l-4 border-forest p-4 text-forest text-sm">
              You're signed up. Watch your email for route details a few days before.
            </div>
          )}

          <div className="pt-2 flex justify-end">
            <button
              type="submit"
              disabled={submitting || !selectedId}
              className="bg-terracotta text-cream px-10 py-4 text-sm tracking-[0.1em] uppercase hover:bg-forest transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Signing up…' : 'Sign me up →'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
